// src/common/pages/dniUpload/guiaEnVivo/guia.ts
/**
 * El estado de la guía en vivo, cuadro a cuadro (decisiones del usuario, 2026-10-14):
 *
 *  - un solo mensaje por vez, sin trato («Más cerca»), con prioridad (analizarCuadro); un mensaje nuevo
 *    se muestra cuando dura un poco, para que no parpadee;
 *  - con las cuatro esquinas, la banda de texto nítida y sin reflejo durante ~0,7 s, el
 *    marco se pone verde y se pide leer una banda corta y fija (el encabezado del frente,
 *    el comienzo del MRZ del dorso), a lo sumo una vez por segundo;
 *  - si la banda se lee, la foto se saca sola en el cuadro siguiente, si sigue verde (tilde y
 *    vibración); si no, «Sin mover el documento…» y se vuelve a leer.
 *
 * El dorso 2009 no lleva MRZ: para esa cara, tras unas lecturas fallidas con todo lo demás
 * bien, alcanza la nitidez (`lecturasFallidasSinBanda`). El frente siempre tiene su
 * encabezado (2009, 2012 y 2026): ahí no hay atajo.
 *
 * Sin DOM ni reloj propio: el tiempo entra con cada cuadro.
 */
import { UMBRALES_DE_LA_GUIA } from "./analizarCuadro";
import type { AnalisisDelCuadro, ProblemaDelCuadro } from "./analizarCuadro";

/** «fondo»: sin ningún borde a la vista durante `sinBordes` (una mesa clara), el consejo de una superficie oscura. */
export type MensajeDeLaGuia = ProblemaDelCuadro | "fondo" | "quieto" | "listo";

export interface EstadoDeLaGuia {
  mensaje: MensajeDeLaGuia;
  esquinas: [boolean, boolean, boolean, boolean];
  /** Todo bien durante `sostenido`: el marco en verde, verificando la banda. */
  verde: boolean;
  /** La foto se sacó: tilde. */
  tomada: boolean;
}

export interface OpcionesDeLaGuia {
  /** Cuánto tiene que durar todo bien para ponerse verde, en ms. */
  sostenido: number;
  /** El mínimo entre dos lecturas de la banda, en ms. */
  entreLecturas: number;
  /** Cuánto tiene que durar un mensaje nuevo para mostrarse, en ms. */
  permanenciaDelMensaje: number;
  /** Cuánto sin ningún borde antes de aconsejar una superficie oscura, en ms. */
  sinBordes: number;
  /** Tras cuántas lecturas fallidas alcanza la nitidez (null: nunca). */
  lecturasFallidasSinBanda: number | null;
  nitidezMinima: number;
}

export const OPCIONES_DE_LA_GUIA: OpcionesDeLaGuia = {
  sostenido: 700,
  entreLecturas: 1000,
  permanenciaDelMensaje: 400,
  sinBordes: 3000,
  lecturasFallidasSinBanda: null,
  nitidezMinima: UMBRALES_DE_LA_GUIA.nitidezMinima.frente,
};

export class GuiaEnVivo {
  private readonly o: OpcionesDeLaGuia;
  private _estado: EstadoDeLaGuia = { mensaje: "ubicar", esquinas: [false, false, false, false], verde: false, tomada: false };
  private desdeQueEstaBien: number | null = null;
  private desdeQueNoHayBordes: number | null = null;
  private candidato: MensajeDeLaGuia = "ubicar";
  private desdeElCandidato = 0;
  private leyendo = false;
  private ultimaLectura: number | null = null;
  private fallidas = 0;
  /** La banda se leyó (o alcanza la nitidez): la foto se saca en el cuadro siguiente, si sigue verde. */
  private porSacar = false;

  constructor(opciones: Partial<OpcionesDeLaGuia> = {}) {
    this.o = { ...OPCIONES_DE_LA_GUIA, ...opciones };
  }

  get estado(): EstadoDeLaGuia {
    return this._estado;
  }

  /** Un cuadro analizado y la nitidez de su banda (null si no se pudo medir). */
  cuadro(
    a: AnalisisDelCuadro,
    nitidez: number | null,
    ahora: number
  ): { estado: EstadoDeLaGuia; leer: boolean; capturar: boolean } {
    if (this._estado.tomada) return { estado: this._estado, leer: false, capturar: false };
    const movida = nitidez === null || nitidez < this.o.nitidezMinima;
    const bien = a.problema === null && !movida;
    let verde = false;
    if (bien) {
      if (this.desdeQueEstaBien === null) this.desdeQueEstaBien = ahora;
      verde = ahora - this.desdeQueEstaBien >= this.o.sostenido;
    } else {
      this.desdeQueEstaBien = null;
      this.fallidas = 0;
    }
    // La foto es la de este cuadro: se saca solo si sigue verde. La lectura tardó (hasta ~1 s en un
    // celular) y el cuadro que se verificó antes de leer ya no es el que se fotografía.
    if (this.porSacar) {
      this.porSacar = false;
      if (verde) {
        this._estado = { mensaje: "listo", esquinas: a.esquinas, verde: true, tomada: true };
        return { estado: this._estado, leer: false, capturar: true };
      }
    }
    // Ningún borde (ninguna esquina, y nada más concreto que decir) durante unos segundos: en una
    // mesa clara el borde del DNI casi no se ve. El consejo es solo para ese caso, no para todos.
    const sinBordes = a.problema === "ubicar" && !a.esquinas.some(Boolean);
    if (!sinBordes) this.desdeQueNoHayBordes = null;
    else if (this.desdeQueNoHayBordes === null) this.desdeQueNoHayBordes = ahora;
    const fondo = sinBordes && ahora - (this.desdeQueNoHayBordes ?? ahora) >= this.o.sinBordes;
    // Con todo bien, mientras se sostiene y se verifica la banda: «Quédese quieto…».
    const deseado: MensajeDeLaGuia = fondo ? "fondo" : a.problema ?? "quieto";
    let mensaje = this._estado.mensaje;
    if (deseado !== this.candidato) {
      this.candidato = deseado;
      this.desdeElCandidato = ahora;
    }
    if (deseado !== mensaje && ahora - this.desdeElCandidato >= this.o.permanenciaDelMensaje) mensaje = deseado;
    const leer =
      verde && !this.leyendo && (this.ultimaLectura === null || ahora - this.ultimaLectura >= this.o.entreLecturas);
    if (leer) {
      this.leyendo = true;
      this.ultimaLectura = ahora;
    }
    this._estado = { mensaje, esquinas: a.esquinas, verde, tomada: false };
    return { estado: this._estado, leer, capturar: false };
  }

  /**
   * El resultado de la lectura de la banda que se pidió. Si alcanza, la foto se saca en el cuadro
   * siguiente (`cuadro` devuelve `capturar`), si sigue verde.
   */
  lectura(leida: boolean, ahora: number): { estado: EstadoDeLaGuia } {
    this.leyendo = false;
    // Si mientras se leía se perdió el encuadre, lo leído ya no es de lo que se ve.
    if (this._estado.tomada || !this._estado.verde) return { estado: this._estado };
    if (!leida) this.fallidas++;
    const alcanza =
      leida || (this.o.lecturasFallidasSinBanda !== null && this.fallidas >= this.o.lecturasFallidasSinBanda);
    if (!alcanza) {
      this.candidato = "quieto";
      this.desdeElCandidato = ahora;
      this._estado = { ...this._estado, mensaje: "quieto" };
      return { estado: this._estado };
    }
    this.porSacar = true;
    return { estado: this._estado };
  }
}
